import React, { useCallback, useState } from "react";
import { connect } from "react-redux";

import Form from "antd/es/form";
import Radio from "antd/es/radio";
import { useIntl } from "react-intl";

import Team from "@mapmycustomers/shared/types/Team";
import { Modal, ModalProps, TeamMultiSelectField, TextField } from "@mapmycustomers/ui";

import RadioGroup from "@app/component/input/RadioGroup";
import { overrideTeamDashboards } from "@app/scene/dashboard/store/actions";
import OverrideDashboardsFormValues from "@app/scene/dashboard/types/OverrideDashboardsFormValues";
import { getTeams } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";

import styles from "./HeaderCommon.module.scss";
import messages from "./messages";

const CONFIRM_DELETE_TEXT = "Apply org dashboard to teams";

interface Props extends Pick<ModalProps, "open"> {
  onModalOpen: (open: boolean) => void;
  onOverrideTeamDashboards: typeof overrideTeamDashboards.request;
  teams: Team[];
}

const DashboardOverrideModal: React.FC<Props> = ({
  onModalOpen,
  onOverrideTeamDashboards,
  open,
  teams,
}) => {
  const intl = useIntl();

  const [form] = Form.useForm<OverrideDashboardsFormValues>();
  const allTeamsUsers = Form.useWatch("allTeamsUsers", form);

  const [deleteText, setDeleteText] = useState<string>("");

  const handleApplyOrgDashboard = useCallback(() => {
    const formValues = form.getFieldsValue();
    onOverrideTeamDashboards(formValues);
    form.resetFields();
    onModalOpen(false);
  }, [form, onModalOpen, onOverrideTeamDashboards]);

  const handleCancel = useCallback(() => {
    setDeleteText("");
    form.resetFields();
    onModalOpen(false);
  }, [form, onModalOpen]);

  return (
    <Modal
      cancelText={intl.formatMessage(messages.cancel)}
      okButtonProps={{
        danger: true,
        disabled: deleteText !== CONFIRM_DELETE_TEXT,
        type: "default",
      }}
      okText={intl.formatMessage(messages.apply)}
      onCancel={handleCancel}
      onOk={handleApplyOrgDashboard}
      open={open}
      title={intl.formatMessage(messages.applyOrgDashboardModalTitle)}
    >
      <div className={styles.container}>
        <Form
          form={form}
          initialValues={{
            allTeamsUsers: true,
            teams: [],
          }}
          layout="vertical"
        >
          <Form.Item label={intl.formatMessage(messages.applyToFollowing)} name="allTeamsUsers">
            <RadioGroup>
              <Radio value={true}>{intl.formatMessage(messages.allTeamsUsers)}</Radio>
              <Radio value={false}>{intl.formatMessage(messages.specificTeams)}</Radio>
            </RadioGroup>
          </Form.Item>
          {!allTeamsUsers && (
            <Form.Item label={intl.formatMessage(messages.selectTeams)} name="teamIds">
              <TeamMultiSelectField oneLiner teams={teams} />
            </Form.Item>
          )}
        </Form>
        <span>
          {intl.formatMessage(messages.modalDescription, {
            allTeams: allTeamsUsers,
            b: (text) => <b>{text}</b>,
          })}
        </span>
        <div className={styles.input}>
          <TextField
            label={intl.formatMessage(messages.helpText, { b: (text) => <b>{text}</b> })}
            onChange={setDeleteText}
            value={deleteText}
          />
        </div>
      </div>
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  teams: getTeams(state),
});

const mapDispatchToProps = {
  onOverrideTeamDashboards: overrideTeamDashboards.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(DashboardOverrideModal);
